import React, { useEffect, useState } from 'react'
import { useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom'
import TestIds from '../../../testIds'
import { PageContainer } from '../../../components/layout/PageContainer'
import { Loader } from '../../../components/Loader'
import { BackButton } from '../../../components/BackButton'
import { ProductDetailsApiResponse } from '../storage/types'
import { Breadcrumbs } from '../../../components/Breadcrumbs'
import { ProductDetailsTabs } from '../components/ProductDetails/ProductDetailsTabs'
import { ProductInfo, safeQuantity } from '../components/ProductDetails/ProductInfo'
import ContactCard from '../../../components/ContactCard'
import { CONTACT_CARD_TITLE, CONTENT_LAYOUT_WIDTH } from '../../../util/consts'
import { selectContactInfo, selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { useSelector } from 'react-redux'
import { styled } from '@mui/system'
import QuoteProvider from '../components/Quotes/useQuoteModal'
import { AxiosError } from 'axios'
import { usePreviousPage } from '../../../util/usePreviousPage'
import { ProductType } from '../components/ProductCard'
import { useAppDispatch } from '../../../store/rootStore'
import { StocksSlice } from '../storage/stocks.slice'
import { fetchProductDetail } from '../utils/fetchProductDetail'
import { useBranch } from '../../../util/useBranch'

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
})

const ProductDetails = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { previousPage } = usePreviousPage()
  const { masterProductId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sync query parameters with state
  const [queryVariant, setQueryVariant] = useState({
    size: searchParams.get('size') ?? '',
    colour: searchParams.get('colour') ?? '',
    qty: safeQuantity(searchParams.get('qty')),
  })

  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const relationshipOwner = useSelector(selectContactInfo)
  const [item, setItem] = useState<ProductDetailsApiResponse | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<AxiosError>()
  const branchId = useBranch()

  // Sync component state to query parameters
  useEffect(() => {
    setSearchParams({
      size: queryVariant.size,
      colour: queryVariant.colour,
      qty: queryVariant.qty.toString(),
    })
  }, [queryVariant, setSearchParams])

  // Sync query parameters back to component state on change
  useEffect(() => {
    setQueryVariant({
      size: searchParams.get('size') ?? '',
      colour: searchParams.get('colour') ?? '',
      qty: safeQuantity(searchParams.get('qty')),
    })
  }, [searchParams])

  // Fetch product details based on query parameters and state
  useEffect(() => {
    if (masterProductId) {
      setIsLoading(true)
      fetchProductDetail(
        masterProductId,
        currentAccount?.accountNumber,
        branchId,
        queryVariant.size,
        queryVariant.colour,
        location.state,
      )
        .then((newItem) => {
          setItem(newItem)
          dispatch(StocksSlice.actions.addStock(newItem))
          setError(undefined)
          setIsLoading(false)
        })
        .catch((err) => {
          setError(err as AxiosError)
          console.log('error', error)
        })
    }
  }, [
    currentAccount?.accountNumber,
    masterProductId,
    branchId,
    // Do not include: queryVariant,
    location.state,
    dispatch,
  ])

  const getBreadcrumbURL = (categoryType: 'main' | 'sub') => {
    let category = item?.category ?? ''
    if (categoryType === 'sub') {
      category += `>${item?.subCategory}`
    }
    const tab = location.state?.productType === ProductType.Past ? '0' : '1'
    const params = new URLSearchParams({
      tab,
      category,
    })
    return `/products?${params.toString()}`
  }

  const navigateToBack = () => {
    if (!location.state) navigate('/products?tab=1')
    else if (previousPage === '/home') navigate('/products?tab=1')
    else navigate(-1)
  }

  return (
    <PageContainer
      item-testid={TestIds.pages.productDetails}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        maxWidth: CONTENT_LAYOUT_WIDTH,
      }}
    >
      {isLoading && <Loader />}
      {!isLoading && item && (
        <Wrapper sx={{ gap: 4 }}>
          <Wrapper>
            <BackButton
              text='Back to products'
              onClick={navigateToBack}
              data-testid={TestIds.components.backButton.productDetails}
            />
            <Breadcrumbs
              items={[
                {
                  text: item.category,
                  href: getBreadcrumbURL('main'),
                },
                {
                  text: item.subCategory ?? '',
                  href: getBreadcrumbURL('sub'),
                },
                { text: item.name },
              ]}
            />
            <ProductInfo
              item={item}
              queryVariant={queryVariant}
              setQueryVariant={setQueryVariant}
              data-testid={TestIds.feat.product.details.container}
            />
          </Wrapper>
          <ProductDetailsTabs item={item} />
          <ContactCard title={CONTACT_CARD_TITLE.productDetails} {...relationshipOwner} />
        </Wrapper>
      )}
    </PageContainer>
  )
}

export default () => QuoteProvider(ProductDetails)
